import React, { createContext, useContext, useEffect, useState } from "react";
import {
  UPLOAD_USER_AVATAR_API,
  FETCH_USER_AVATAR_API,
  USER_DETAILS_API,
  GET_PAGE_ROLES_BY_USER_API,
  LOGGED_IN_USER_EMAIL_API,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [selectedUserDetailLoading, setSelectedUserDetailLoading] =
    useState(true);
  const [selectedUserDetailError, setSelectedUserDetailError] = useState(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState({});
  const [loggedInUserEmail, setLoggedInUserEmail] = useState(null);
  const [loggedInUserEmailLoading, setLoggedInUserEmailLoading] =
    useState(false);
  const [loggedInUserEmailError, setLoggedInUserEmailError] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApplicationAdministrator, setIsApplicationAdministrator] =
    useState(false);
  const [isGlobalReadUser, setIsGlobalReadUser] = useState(false);
  const [isGlobalWriteUser, setIsGlobalWriteUser] = useState(false);

  // State for avatar-related data
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);

  const [userPageRolesLoading, setUserPageRolesLoading] = useState(false);
  const [userPageRolesError, setUserPageRolesError] = useState(null);
  const [userPageRoles, setUserPageRoles] = useState([]);

  const fetchLoggedInUserEmail = () => {
    setLoggedInUserEmailLoading(true);
    setLoggedInUserEmailError(null);
    securedFetch(LOGGED_IN_USER_EMAIL_API, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch the current logged-in user email");
        }
        return res?.text();
      })
      .then((email) => {
        setLoggedInUserEmail(email || null);
      })
      .catch((error) => {
        setLoggedInUserEmailError(error.message);
      })
      .finally(() => {
        setLoggedInUserEmailLoading(false);
      });
  };

  useEffect(() => {
    setLoggedInUserEmailLoading(true);
    setLoggedInUserEmailError(null);
    fetchLoggedInUserEmail();
  }, []);

  const fetchAvatar = async (email) => {
    if (!email) return;

    setAvatarLoading(true);
    setAvatarError(null);
    try {
      const response = await securedFetch(FETCH_USER_AVATAR_API(email), {
        method: "GET",
      });
      if (response.ok) {
        const blob = await response.blob();
        setAvatarUrl(URL.createObjectURL(blob));
      } else {
        setAvatarUrl(null);
      }
    } catch (error) {
      console.error("Failed to fetch avatar:", error);
      setAvatarError(error.message);
      setAvatarUrl(null);
    } finally {
      setAvatarLoading(false);
    }
  };

  const uploadAvatar = async (email, file) => {
    if (!email || !file) return;

    setAvatarLoading(true);
    setAvatarError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await securedFetch(UPLOAD_USER_AVATAR_API(email), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload file. Status: ${response.status}`);
      }

      // After successful upload, immediately fetch the new avatar to update the UI
      await fetchAvatar(email);
    } catch (error) {
      console.error("Error uploading file:", error.message);
      setAvatarError(error.message);
    } finally {
      setAvatarLoading(false);
    }
  };

  useEffect(() => {
    if (loggedInUserEmail) {
      setSelectedUserDetailLoading(true);
      setSelectedUserDetailError(null);
      securedFetch(USER_DETAILS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: loggedInUserEmail,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch List of Regions and SFCs");
          }
          return res.json();
        })
        .then((data) => {
          setSelectedUserDetail(data || {});
          const {
            active,
            admin,
            applicationAdministrator,
            globalRead,
            globalWrite,
          } = data || {};
          setIsActive(active);
          setIsAdmin(admin);
          setIsApplicationAdministrator(applicationAdministrator);
          setIsGlobalReadUser(globalRead);
          setIsGlobalWriteUser(globalWrite);
          // Fetch the avatar after fetching user details
          fetchAvatar(data?.email);
        })
        .catch((error) => {
          setSelectedUserDetailError(error.message);
        })
        .finally(() => {
          setSelectedUserDetailLoading(false);
        });
    }
  }, [loggedInUserEmail]);

  useEffect(() => {
    const fetchPageRoles = async () => {
      if (!loggedInUserEmail) {
        return;
      }

      setUserPageRolesLoading(true);
      setUserPageRolesError(null);

      try {
        const response = await securedFetch(GET_PAGE_ROLES_BY_USER_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: loggedInUserEmail,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch the Page Role by Email");
        }

        const data = await response.json();
        setUserPageRoles(data || {});
      } catch (error) {
        console.error("Failed to fetch the Page Role by Email Error:", error);
        setUserPageRoles({});
        setUserPageRolesError(error?.message);
      } finally {
        setUserPageRolesLoading(false);
      }
    };

    fetchPageRoles();
  }, [loggedInUserEmail]);

  return (
    <UserContext.Provider
      value={{
        loggedInUserEmail,
        setLoggedInUserEmail,
        loggedInUserEmailLoading,
        setLoggedInUserEmailLoading,
        loggedInUserEmailError,
        setLoggedInUserEmailError,
        selectedUserDetailLoading,
        selectedUserDetailError,
        selectedUserDetail,
        setSelectedUserDetail,
        isActive,
        isAdmin,
        isApplicationAdministrator,
        isGlobalReadUser,
        isGlobalWriteUser,

        avatarUrl,
        setAvatarUrl,
        avatarLoading,
        avatarError,
        uploadAvatar,

        userPageRolesLoading,
        setUserPageRolesLoading,
        userPageRolesError,
        setUserPageRolesError,
        userPageRoles,
        setUserPageRoles,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
